import api from './api';

export interface CustomerTransaction {
  id: number;
  customerCurrentAccountId: number;
  customerId: number;
  cashBoxId: number;
  cashBoxName: string;
  type: 'INGRESO' | 'PAGO' | 'CARGO';
  description: string;
  amount: number;
  createdAt: string;
}

export interface CustomerCurrentAccount {
  id: number;
  customerId: number;
  balance: number;
  totalIncome: number;
  totalPayments: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Payload para crear transacciones de cuenta corriente de clientes
 *
 * NOTA: Internamente el backend usa el nuevo sistema de transacciones,
 * pero la API sigue manteniendo compatibilidad con los tipos:
 * - INGRESO: Crea un Depósito (Debita Caja, Acredita Cliente)
 * - PAGO/EGRESO: Crea un Retiro (Acredita Caja, Debita Cliente)
 * - CARGO: Ajuste manual (Debita Cliente, Acredita Ingresos Varios)
 */
export interface CreateTransactionPayload {
  customerId: number;
  cashBoxId: number | null;
  type: 'INGRESO' | 'PAGO' | 'CARGO';
  description: string;
  amount: number;
}

interface CustomerTransactionsResponse {
  success: boolean;
  message: string;
  data: CustomerTransaction[];
  timestamp: string;
}

interface CustomerAccountResponse {
  success: boolean;
  message: string;
  data: CustomerCurrentAccount;
  timestamp: string;
}

interface SingleTransactionResponse {
  success: boolean;
  message: string;
  data: CustomerTransaction;
  timestamp: string;
}

interface AllAccountsResponse {
  success: boolean;
  message: string;
  data: CustomerCurrentAccount[];
  timestamp: string;
}

const customerCurrentAccountService = {
  getAllAccounts: async (): Promise<CustomerCurrentAccount[]> => {
    try {
      const response = await api.get<AllAccountsResponse>('/customer-current-account');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching all current accounts:', error);
      throw error;
    }
  },

  /**
   * Obtener todas las transacciones de un cliente
   *
   * @param customerId - ID del cliente
   * @returns Lista de transacciones del cliente
   */
  getTransactionsByCustomer: async (
    customerId: number,
  ): Promise<CustomerTransaction[]> => {
    try {
      const response = await api.get<CustomerTransactionsResponse>(
        `/customer-current-account/transaction/customer/${customerId}`,
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching customer transactions:', error);
      throw error;
    }
  },

  /**
   * Obtener el balance de la cuenta corriente del cliente
   *
   * @param customerId - ID del cliente
   * @returns Información de la cuenta corriente con balance y totales
   */
  getCustomerAccountBalance: async (
    customerId: number,
  ): Promise<CustomerCurrentAccount> => {
    try {
      const response = await api.get<CustomerAccountResponse>(
        `/customer-current-account/customer/${customerId}`,
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching customer account balance:', error);
      throw error;
    }
  },

  /**
   * Crear una nueva transacción en la cuenta corriente del cliente
   *
   * NOTA IMPORTANTE: Este endpoint usa internamente el nuevo sistema de transacciones.
   * Los tipos de transacción se mapean de la siguiente manera:
   * - INGRESO: Crea un Depósito (Debita Caja, Acredita Cliente)
   * - PAGO/EGRESO: Crea un Retiro (Acredita Caja, Debita Cliente)
   * - CARGO: Ajuste manual (Debita Cliente, Acredita Ingresos Varios)
   *
   * @param payload - Datos de la transacción
   * @returns Transacción creada
   */
  createTransaction: async (
    payload: CreateTransactionPayload,
  ): Promise<CustomerTransaction> => {
    try {
      const response = await api.post<SingleTransactionResponse>(
        '/customer-current-account/transaction',
        payload,
      );
      return response.data.data;
    } catch (error) {
      console.error('Error creating transaction:', error);
      throw error;
    }
  },
};

export default customerCurrentAccountService;
